import { APP_CONFIG } from '@/constants/app';
import {
  CreateOrganisationDto,
  FolderAccess,
  InviteMemberDto,
  Organisation,
  OrganisationInvitation,
  OrganisationMember,
  OrganisationResponsable,
  RegisterFromOrgInvitationData,
  UpdateMemberRoleDto,
  UpdateOrganisationDto,
} from '@/types/organisation.types';
import { AuthResponse } from '@/types/auth.types';
import { unwrapApiData } from '@/utils/api-response';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

const organisationsApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/organisations',
  headers: { 'Content-Type': 'application/json' },
});

const domainsApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/domains',
  headers: { 'Content-Type': 'application/json' },
});

tokenManager.setupInterceptors(organisationsApi);
tokenManager.setupInterceptors(domainsApi);

export const organisationsService = {
  getOrganisations: async (): Promise<Organisation[]> => {
    const response = await organisationsApi.get<Organisation[]>('/');
    const data = response.data;
    return unwrapApiData(data);
  },

  getOrganisation: async (uuid: string): Promise<Organisation> => {
    const response = await organisationsApi.get<Organisation>(`/${uuid}`);
    const data = response.data;
    return unwrapApiData(data);
  },

  createOrganisation: async (
    dto: CreateOrganisationDto
  ): Promise<Organisation> => {
    const response = await organisationsApi.post<Organisation>('/', dto);
    const data = response.data;
    return unwrapApiData(data);
  },

  updateOrganisation: async (
    uuid: string,
    dto: UpdateOrganisationDto
  ): Promise<Organisation> => {
    const response = await organisationsApi.put<Organisation>(`/${uuid}`, dto);
    const data = response.data;
    return unwrapApiData(data);
  },

  getMembers: async (uuid: string): Promise<OrganisationMember[]> => {
    const response = await organisationsApi.get<OrganisationMember[]>(
      `/${uuid}/members`
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  // Valeurs distinctes de responsable_dossier (avec nombre de dossiers) sur les
  // domaines de l'organisation. Réservé admin côté API. Sert à assigner les alias.
  getResponsables: async (uuid: string): Promise<OrganisationResponsable[]> => {
    const response = await organisationsApi.get<OrganisationResponsable[]>(
      `/${uuid}/responsables`
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  inviteMember: async (
    uuid: string,
    dto: InviteMemberDto
  ): Promise<OrganisationMember> => {
    const response = await organisationsApi.post<OrganisationMember>(
      `/${uuid}/invite`,
      dto
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  updateMemberRole: async (
    uuid: string,
    memberUuid: string,
    dto: UpdateMemberRoleDto
  ): Promise<OrganisationMember> => {
    const response = await organisationsApi.put<OrganisationMember>(
      `/${uuid}/members/${memberUuid}`,
      dto
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  removeMember: async (uuid: string, memberUuid: string): Promise<void> => {
    await organisationsApi.delete(`/${uuid}/members/${memberUuid}`);
  },

  leaveOrganisation: async (uuid: string): Promise<void> => {
    await organisationsApi.post(`/${uuid}/leave`);
  },

  // --- Invitations ---

  getInvitationByToken: async (
    token: string
  ): Promise<OrganisationInvitation> => {
    const response = await organisationsApi.get<OrganisationInvitation>(
      `/invitation/${token}`
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  acceptInvitation: async (token: string): Promise<OrganisationMember> => {
    const response = await organisationsApi.post<OrganisationMember>(
      `/invitation/${token}/accept`
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  // Inscription d'un nouveau collaborateur depuis une invitation (endpoint public).
  registerFromInvitation: async (
    token: string,
    payload: RegisterFromOrgInvitationData
  ): Promise<AuthResponse> => {
    const response = await organisationsApi.post<AuthResponse>(
      `/invitation/${token}/register`,
      payload
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  getPendingInvitations: async (
    uuid: string
  ): Promise<OrganisationInvitation[]> => {
    const response = await organisationsApi.get<OrganisationInvitation[]>(
      `/${uuid}/invitations`
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  cancelInvitation: async (
    uuid: string,
    invitationUuid: string
  ): Promise<void> => {
    await organisationsApi.delete(`/${uuid}/invitations/${invitationUuid}`);
  },

  // Domain/Folder archiving & restriction (via domains API)
  archiveDomain: async (
    domainUuid: string,
    archive: boolean
  ): Promise<void> => {
    await domainsApi.put(`/${domainUuid}/archive`, { archive });
  },

  archiveFolder: async (
    folderUuid: string,
    archive: boolean
  ): Promise<void> => {
    await domainsApi.put(`/folders/${folderUuid}/archive`, { archive });
  },

  restrictFolder: async (
    folderUuid: string,
    isRestricted: boolean
  ): Promise<void> => {
    await domainsApi.put(`/folders/${folderUuid}/restrict`, { isRestricted });
  },

  getFolderAccess: async (folderUuid: string): Promise<FolderAccess[]> => {
    const response = await domainsApi.get<FolderAccess[]>(
      `/folders/${folderUuid}/access`
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  grantFolderAccess: async (
    folderUuid: string,
    memberUuid: string
  ): Promise<FolderAccess> => {
    const response = await domainsApi.post<FolderAccess>(
      `/folders/${folderUuid}/access`,
      { memberUuid }
    );
    const data = response.data;
    return unwrapApiData(data);
  },

  revokeFolderAccess: async (
    folderUuid: string,
    memberUuid: string
  ): Promise<void> => {
    await domainsApi.delete(`/folders/${folderUuid}/access/${memberUuid}`);
  },
};
